import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Lock, ShieldCheck } from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { useCurrentReseller } from '../../api/reseller';

/** Reseller-branded sign-in (uses currentReseller.primaryColor for accents). */
export function ResellerLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { data: currentReseller } = useCurrentReseller();
  if (!currentReseller) return null;

  const accent = currentReseller.primaryColor;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => navigate({ to: '/reseller' }), 800);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid lg:grid-cols-2">
      {/* Left — reseller-branded pitch */}
      <div
        className="hidden lg:flex relative overflow-hidden p-12 xl:p-20 flex-col justify-between border-r border-zinc-200 dark:border-zinc-700"
        style={{ backgroundColor: `color-mix(in srgb, ${accent} 4%, transparent)` }}
      >
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <span
              className="w-9 h-9 rounded-lg grid place-items-center text-white font-semibold text-sm"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            >
              {currentReseller.displayName.charAt(0)}
            </span>
            <span className="font-semibold text-lg tracking-tight text-zinc-950 dark:text-zinc-50">
              {currentReseller.displayName}
            </span>
          </div>
        </div>

        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-lg">
            Cloud POS,
            <br />
            <span style={{ color: accent }}>made simple.</span>
          </h1>
          <p className="mt-6 text-base text-zinc-600 dark:text-zinc-300 max-w-md">
            Run your shop, restaurant, or pharmacy on a calm, modern interface — backed by{' '}
            {currentReseller.name} support, every day of the week.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-zinc-700 dark:text-zinc-200">
            <Bullet color={accent}>Local support, local language</Bullet>
            <Bullet color={accent}>BDT-native, SSLCommerz built in</Bullet>
            <Bullet color={accent}>Bring your own database, or use ours</Bullet>
            <Bullet color={accent}>Light + dark, mobile + desktop</Bullet>
          </ul>
        </div>

        <div className="relative text-xs text-zinc-500 dark:text-zinc-400">
          Powered by Possignin · © 2026 ITCity Ltd.
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex flex-col justify-center p-6 sm:p-12">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
          <AuthThemeToggle />
        </div>
        <div className="lg:hidden flex items-center gap-2.5 mb-12">
          <span
            className="w-8 h-8 rounded-lg grid place-items-center text-white font-semibold text-sm"
            style={{ backgroundColor: accent }}
          >
            {currentReseller.displayName.charAt(0)}
          </span>
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {currentReseller.displayName}
          </span>
        </div>

        <div className="w-full max-w-sm mx-auto">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Welcome back
          </h2>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Sign in to your {currentReseller.displayName} account.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <Input
              label="Email"
              type="email"
              required
              placeholder="rakib@acme-resellers.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <div>
              <Input
                label="Password"
                type="password"
                required
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="flex items-center justify-between mt-2">
                <label className="inline-flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300">
                  <input
                    type="checkbox"
                    className="rounded border-zinc-300 dark:border-zinc-600"
                    style={{ accentColor: accent }}
                  />
                  Stay signed in for 7 days
                </label>
                <Link
                  to="/"
                  className="text-xs font-medium hover:underline"
                  style={{ color: accent }}
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md text-white font-medium px-4 py-2.5 text-sm transition disabled:opacity-50"
              style={{ backgroundColor: accent }}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>

            <div className="flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400">
              <Lock className="w-3 h-3" />
              <span>Encrypted · 2-factor available</span>
            </div>
          </form>

          <p className="mt-10 text-sm text-zinc-600 dark:text-zinc-300 text-center">
            New to {currentReseller.displayName}?{' '}
            <Link to="/" className="font-medium hover:underline" style={{ color: accent }}>
              Start a free trial
            </Link>
          </p>

          {/* Powered-by footer */}
          <div className="mt-12 pt-6 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 dark:text-zinc-400">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Powered by</span>
            <span className="font-semibold text-zinc-700 dark:text-zinc-200">Possignin</span>
          </div>
        </div>
      </div>

      {/* Mock submit button (for non-mouse users — use the standard Button on form) */}
      <div className="hidden">
        <Button type="button" />
      </div>
    </div>
  );
}

function Bullet({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color }} />
      <span>{children}</span>
    </li>
  );
}
